/**
 * BetaTag Component
 *
 * A preset Badge component that displays "BETA" text to indicate features
 * that are in beta status.
 *
 * This is a thin wrapper around the Badge component with label defaulting to "BETA".
 *
 * @example
 * ```tsx
 * <BetaTag size="sm" />
 * <BetaTag size="lg" />
 * <BetaTag label="ALPHA" /> // Override label if needed
 * ```
 */
import { Badge, type BadgeSize, type BadgeVariant } from "../Badge";

/**
 * Size variants for the BetaTag component
 * @deprecated Use BadgeSize from Badge component instead
 */
export type BetaTagSize = BadgeSize;

/**
 * Visual variants for the BetaTag component
 * @deprecated Use BadgeVariant from Badge component instead
 */
export type BetaTagVariant = BadgeVariant;

/**
 * Props for the BetaTag component
 */
export interface BetaTagProps {
  /**
   * Size variant of the tag
   * - "sm": Small size (9px text, compact padding)
   * - "lg": Large size (standard body text, larger padding)
   * @default "sm"
   */
  size?: BetaTagSize;

  /**
   * Visual variant of the tag
   * - "default": Subtle neutral background
   * - "on-dark": High-contrast option for dark surfaces
   * @default "default"
   */
  variant?: BetaTagVariant;

  /**
   * Additional CSS classes to apply to the tag
   */
  className?: string;

  /**
   * Text content rendered inside the tag. Defaults to "BETA".
   */
  label?: string;
}

/**
 * BetaTag Component
 *
 * Displays a "BETA" badge to indicate features in beta status.
 * Thin wrapper around the Badge component.
 *
 * @param props - Component props (@see BetaTagProps)
 * @returns A styled BETA badge component
 */
export const BetaTag = ({
  size = "sm",
  variant = "default",
  className = "",
  label = "BETA",
}: BetaTagProps) => {
  return (
    <Badge label={label} size={size} variant={variant} className={className} />
  );
};
